'use client'

import { useEffect, useRef } from 'react'

type Dot = {
  x: number
  y: number
  jitter: number
}

interface HeroBackgroundProps {
  /** Grid spacing in px. Smaller = denser dot field. Default 14. */
  spacing?: number
  /** Multiplier on dot radius. <1 shrinks dots. Default 1. */
  dotScale?: number
  /** Time multiplier. <1 = slower/smoother motion. Default 1. */
  speed?: number
  /** When true, the field is brighter on the right (where the hero
   *  Soleno stencil sits). Disable for evenly-distributed sections. */
  rightBias?: boolean
  /** Boosts both ambient and bright dot alpha for a more present field. */
  tonedUp?: boolean
  /** Locks dot colour to brand-500 #7D40FF (no lerp toward lavender). */
  pureBrand?: boolean
}

export default function HeroBackground({
  spacing = 14,
  dotScale = 1,
  speed = 1,
  rightBias = true,
  tonedUp = false,
  pureBrand = false,
}: HeroBackgroundProps = {}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return
    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    // ---- Tunables (props with sensible hero defaults) ----
    const SPACING = spacing

    let dpr = 1
    let width = 0
    let height = 0
    let dots: Dot[] = []

    // Only the halftone noise field is painted here.
    let visible = true
    let rafId = 0

    const buildScene = () => {
      const rect = container.getBoundingClientRect()
      width = rect.width
      height = rect.height
      dpr = Math.min(window.devicePixelRatio || 1, 2)

      canvas.width = Math.floor(width * dpr)
      canvas.height = Math.floor(height * dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      const next: Dot[] = []
      const cols = Math.ceil(width / SPACING) + 1
      const rows = Math.ceil(height / SPACING) + 1
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const x = c * SPACING + (r % 2 === 0 ? 0 : SPACING / 2)
          const y = r * SPACING
          if (x < 0 || x > width || y < 0 || y > height) continue
          const seed = Math.sin(c * 12.9898 + r * 78.233) * 43758.5453
          const jitter = seed - Math.floor(seed)
          next.push({ x, y, jitter })
        }
      }
      dots = next
    }

    // Cheap "fbm-style" smooth noise built from summed sin/cos at multiple
    // frequencies and directions. Returns roughly [0..1].
    // This produces large, slow, organic drifting bright regions instead
    // of discrete blobs sweeping across the canvas.
    const noiseAt = (x: number, y: number, t: number) => {
      const n1 =
        Math.sin(x * 0.0055 + t * 0.30) *
        Math.cos(y * 0.0048 - t * 0.22)
      const n2 =
        Math.sin((x + y) * 0.0085 - t * 0.34) * 0.65
      const n3 =
        Math.sin(x * 0.018 + t * 0.55) *
        Math.cos(y * 0.014 + t * 0.42) * 0.32
      const n4 =
        Math.sin((x - y * 0.7) * 0.0032 + t * 0.18) * 0.45
      return (n1 + n2 + n3 + n4) * 0.4 + 0.5 // ~0..1
    }

    const draw = (t: number) => {
      rafId = requestAnimationFrame(draw)
      if (!visible) return

      const time = t * 0.001
      const tScale = (prefersReduced ? 0.3 : 1.0) * speed

      ctx.clearRect(0, 0, width, height)

      // Right-side bias — animation feels heavier toward the right where
      // the Soleno logo stencil sits. uvx∈[0..1] from left to right.
      // Multiplier ranges roughly 0.55 (left) → 1.45 (right).
      const widthSafe = Math.max(1, width)

      // Alpha boosts when the section wants a more "toned-up" field.
      const ambientBoost = tonedUp ? 1.8 : 1
      const brightBoost = tonedUp ? 1.35 : 1

      for (let i = 0; i < dots.length; i++) {
        const d = dots[i]

        const uvx = d.x / widthSafe
        const bias = rightBias ? 0.55 + uvx * 0.9 : 1

        // Organic flowing field — drifts naturally, not discrete blobs
        const field = noiseAt(d.x, d.y, time * tScale) * bias

        // Map field to opacity / size
        // Below softFloor → very subtle dim purple/white speck
        // Above           → brand-purple dot rising in alpha & size
        const softFloor = 0.55

        if (field < softFloor) {
          // Smallest ambient dot — drawn for EVERY grid cell so the texture
          // covers the full hero, not just the active/bright regions.
          const norm = Math.max(0, field) / softFloor
          const alpha = (0.02 + 0.05 * norm) * (0.45 + d.jitter * 0.65) * ambientBoost
          // In pureBrand mode the ambient speck is also brand-tinted.
          ctx.fillStyle = pureBrand
            ? `rgba(125,64,255,${alpha})`
            : `rgba(185,165,235,${alpha})`
          ctx.beginPath()
          ctx.arc(d.x, d.y, 0.7 * dotScale, 0, Math.PI * 2)
          ctx.fill()
        } else {
          const norm = Math.min(1, (field - softFloor) / (1 - softFloor))
          const radius = (0.9 + norm * 1.9) * dotScale
          const alpha = (0.10 + norm * 0.28) * (0.65 + d.jitter * 0.4) * brightBoost
          // Brand purple #7D40FF — locked when pureBrand, otherwise
          // lerped to lavender highlight at peak as in the hero.
          const r = pureBrand ? 125 : Math.round(125 + 50 * norm)
          const g = pureBrand ? 64 : Math.round(64 + 70 * norm)
          const b = 255
          ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`
          ctx.beginPath()
          ctx.arc(d.x, d.y, radius, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    }

    buildScene()
    rafId = requestAnimationFrame(draw)

    const ro = new ResizeObserver(() => buildScene())
    ro.observe(container)

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) visible = e.isIntersecting
      },
      { threshold: 0 }
    )
    io.observe(container)

    return () => {
      cancelAnimationFrame(rafId)
      ro.disconnect()
      io.disconnect()
    }
  }, [spacing, dotScale, speed, rightBias, tonedUp, pureBrand])

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden"
      aria-hidden="true"
      style={{
        background:
          'radial-gradient(ellipse at 70% 45%, rgba(125,64,255,0.035) 0%, rgba(125,64,255,0) 60%), #08070d',
      }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: 'none' }}
      />
    </div>
  )
}
